import { Box, Breadcrumbs, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import APIInvoke from '../../utils/APIInvoke'
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from 'react-hot-toast';
import AppContext from '../../state/AppContext';
import { setLoading } from '../../template/app-actions';
import FilterBuilder from '../../components/FilterBuilder/FilterBuilder';
import TextFilter from '../../components/FilterBuilder/controls/TextFilter';
import { hasText } from '../../utils/StringUtils';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Header from '../../template/Header';
import { useSelector } from 'react-redux';
import SearchFilter from '../../components/FilterBuilder/controls/SearchFilter';
import { fromISOToUTCFormat } from '../../utils/DateUtilsUTC';
import RangeDateFilter from '../../components/FilterBuilder/controls/RangeDateFilter';
import { DateTime } from 'luxon';


const columns = [
    {
        field: 'collection',
        headerName: 'Collection',
        type: 'string',
        align: 'left'
    }, {
        field: 'documentId',
        headerName: 'Document Id',
        type: 'string',
        align: 'left'
    }, {
        field: 'documentKey',
        headerName: 'Document Key',
        type: 'string',
        align: 'left'
    }, {
        field: 'timestamp',
        headerName: 'Timestamp',
        type: 'string',
        align: 'left'
    }, {
        field: 'count',
        headerName: 'Count',
        type: 'int',
        align: 'right'
    }
]



const initialState = {
    queryBuilder: {
        queryString: "?sort=timestamp,desc"
    },
    pageSize: 10,
    totalItems: 0,
    currentPage: 0,
    totalPages: 0,
    items: []
}


const HighFrequencyAlerts = (props) => {

    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(initialState)
    const { appContext } = useContext(AppContext)
    const project = appContext.project
    const { loading } = useSelector(state => state.pages.app)
    const [showDeleteDialog, setShowDeleteDialog] = useState({ open: false, payload: null })

    useEffect(() => {
        try {
            setLoading(true)
            init(data)
        } catch (error) {
            console.error("Projects.useEffect => ", error)
        } finally {
            setLoading(false)
        }
    }, [project, data.currentPage, data.pageSize])

    const init = async (newState) => {
        try {
            setLoading(true)
            const query = newState.queryBuilder.queryString
            const response = await (await APIInvoke.invokeGET(`/projects/${project}/high-frequency-alerts${newState.queryBuilder.queryString}${hasText(query) ? "&" : "?"}page=${newState.currentPage}&size=${newState.pageSize || 10}&sort=timestamp,desc`)).json()
            if (response.ok) {
                setData({
                    ...newState,
                    items: response.body.items,
                    totalItems: response.body.totalItems,
                    currentPage: response.body.currentPage,
                    totalPages: response.body.totalPages,
                    pageSize: response.body.pageSize
                });
            } else {
                toast.error(response.message)
            }
        } catch (error) {
            console.error("Error lo load page", error)
        } finally {
            setLoading(false)
        }
    }

    const getFilters = () => {
        return [
            {
                id: "collection",
                name: "Collection",
                placeholder: "Collection",
                filter: "collection",
                type: SearchFilter,
                value: { key: "", value: "" },
                values: async (props) => {
                    const response = await (await APIInvoke.invokeGET(`/projects/${project}/collections?name[cn]=${props.value}`)).json()
                    if (response.ok) {
                        return response.body.items.map(item => ({ key: item.name, value: item.name }))
                    } else {
                        return []
                    }
                },
                getOptionLabel: (option) => {
                    return option.key
                },
                isOptionEqualToValue: (option, value) => {
                    return option.key === value
                },
                processInput: (value) => {
                    return value ? value.key : ""
                }
            }, {
                id: "documentKey",
                name: "Document Key",
                placeholder: "Document Key",
                filter: "documentKey[eq]",
                type: TextFilter,
                defaultValue: ""
            }, {
                id: ["timestamp", "timestampTo"],
                name: ["Timestamp", "Timestamp To"],
                placeholder: ["From", "To"],
                filter: "timestamp[bw]",
                type: RangeDateFilter,
                className: "col-span-1 lg:col-span-2",
                value: [DateTime.now().minus({ days: 7 }).startOf("day"), DateTime.now().endOf("day")],
                processInput: (value) => {
                    let [from, to] = value
                    if (to === undefined) {
                        to = from
                    } else if (from === undefined) {
                        from = to
                    }
                    if (from && to) {
                        return [from.startOf('day').toUTC().toISO(), to.endOf('day').toUTC().toISO()]
                    }
                    return "" 
                },
                getOptionLabel: (value) => {
                    let [from, to] = value
                    if (from && to) {
                        return `${from.toFormat("yyyy-MM-dd")} to ${to.toFormat("yyyy-MM-dd")}`
                    }
                    return ""
                }
            }
        ]
    }

    const onFilterProcess = async (queryBuilder) => {
        const newState = {
            ...data,
            queryBuilder: queryBuilder,
            totalItems: 0,
            currentPage: 0,
            totalPages: 0,
            pageSize: 10,
        }
        setData(newState)
        init(newState)
    }


    const onRowClick = (event, row) => {
        navigate(`/projects/${project}/collections/${row.collection}/documents/${row.documentId}`, { state: { backLink: location.pathname } })
    }
    
    const handleChangePage = (e, newPage) => {
        setData({
            ...data,
            currentPage: newPage
        })
    }

    const onRowsPerPageChange = (event) => {
        setData({
            ...initialState,
            items: data.items,
            pageSize: parseInt(event.target.value, 10)
        })
    }

    return (
        <div className="">
            <Header title="High Frequency Alerts" >
                <Breadcrumbs aria-label="breadcrumb" separator="/" className="!text-gray-600" >
                    <Typography ><HomeOutlinedIcon className="text-gray-600" fontSize="small" /></Typography>
                    <Typography ><span className="text-gray-600">High Frequency Alerts</span></Typography>
                </Breadcrumbs>
            </Header>

            <div className="content ">
                <div className="grid gap-8">
                    <div >
                        <Box sx={{ width: '100%' }}>
                            <Paper sx={{ width: '100%', mb: 2 }}>
                                <div>
                                    <FilterBuilder filters={getFilters()} onProcess={onFilterProcess} />
                                </div>
                                {loading && <LinearProgress size="sm" />}
                                <TableContainer>
                                    <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={"small"}  >
                                        <TableHead>
                                            <TableRow>
                                                {columns.map((headCell) => (
                                                    <TableCell key={headCell.field} align={headCell.align} padding={'normal'} >
                                                        <TableSortLabel >
                                                            {headCell.headerName}
                                                        </TableSortLabel>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.items.map((row, index) => {
                                                return (
                                                    <TableRow
                                                        hover
                                                        onClick={(event) => onRowClick(event, row)}
                                                        tabIndex={-1}
                                                        key={row.id}
                                                        sx={{ cursor: 'pointer' }}
                                                    >
                                                        <TableCell align="left">{row.collection}</TableCell>
                                                        <TableCell align="left" width={250}>{row.documentId}</TableCell>
                                                        <TableCell align="left" width={250}>{row.documentKey}</TableCell>
                                                        <TableCell align="left" width={200}>{fromISOToUTCFormat(row.timestamp, 'yyyy/MM/dd HH:mm:ss')}</TableCell>
                                                        <TableCell align="right" width={50}>{row.count}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {loading && <LinearProgress size="sm" />}
                                <TablePagination
                                    rowsPerPageOptions={[10, 20, 50, 100]}
                                    component="div"
                                    rowsPerPage={data.pageSize}
                                    count={data.totalItems}
                                    page={data.currentPage}
                                    disabled={loading}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={onRowsPerPageChange}
                                />
                            </Paper>
                        </Box>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default HighFrequencyAlerts 